import React, { useState, useEffect } from 'react';
import type { User } from '../types';
import { EmptyState } from './EmptyState';
import { Badge } from './Badge';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';
import { Modal } from './Modal';
import Button from './Button';
import ConfirmDialog from './ConfirmDialog';
import { 
  Users, 
  Mail, 
  Calendar, 
  Search, 
  UserX, 
  UserCheck, 
  MoreVertical,
  Edit
} from 'lucide-react';

interface ParticipantsConfigurationProps {
  eventId: string;
}

interface ParticipantRegistration {
  id: string;
  userId: string;
  user: User;
  eventId: string;
  registrationType: 'reviewee' | 'reviewer';
  status: 'pending' | 'approved' | 'rejected';
  registeredAt: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  approvedBy?: string;
  rejectedBy?: string;
  notes?: string;
}

interface ParticipantStats {
  total: number;
  reviewees: number;
  reviewers: number;
  pending: number;
  approved: number;
  rejected: number;
}

const ParticipantsConfiguration: React.FC<ParticipantsConfigurationProps> = ({ eventId }) => {
  const [participants, setParticipants] = useState<ParticipantRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'reviewee' | 'reviewer'>('all');
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantRegistration | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Mock data for development
  const mockParticipants: ParticipantRegistration[] = [
    {
      id: '1',
      userId: 'user1',
      user: {
        id: 'user1',
        email: 'john.doe@example.com',
        firstName: 'John',
        lastName: 'Doe',
        role: 'user',
        createdAt: new Date('2025-08-01'),
        profilePicture: undefined
      },
      eventId,
      registrationType: 'reviewee',
      status: 'pending',
      registeredAt: new Date('2025-08-15'),
    },
    {
      id: '2',
      userId: 'user2',
      user: {
        id: 'user2',
        email: 'jane.smith@company.com',
        firstName: 'Jane',
        lastName: 'Smith',
        role: 'user',
        createdAt: new Date('2025-07-20'),
        profilePicture: undefined
      },
      eventId,
      registrationType: 'reviewer',
      status: 'approved',
      registeredAt: new Date('2025-08-10'),
      approvedAt: new Date('2025-08-12'),
      approvedBy: 'admin1',
    },
    {
      id: '3',
      userId: 'user3',
      user: {
        id: 'user3',
        email: 'mike.wilson@design.edu',
        firstName: 'Mike',
        lastName: 'Wilson',
        role: 'user',
        createdAt: new Date('2025-08-05'),
        profilePicture: undefined
      },
      eventId,
      registrationType: 'reviewee',
      status: 'approved',
      registeredAt: new Date('2025-08-14'),
      approvedAt: new Date('2025-08-16'),
      approvedBy: 'admin1',
    },
    {
      id: '4',
      userId: 'user4',
      user: {
        id: 'user4',
        email: 'sara.johnson@freelance.com',
        firstName: 'Sara',
        lastName: 'Johnson',
        role: 'user',
        createdAt: new Date('2025-07-30'),
        profilePicture: undefined
      },
      eventId,
      registrationType: 'reviewer',
      status: 'rejected',
      registeredAt: new Date('2025-08-12'),
      rejectedAt: new Date('2025-08-14'),
      rejectedBy: 'admin1',
      notes: 'Insufficient experience for this event level'
    }
  ];

  // Load participants
  useEffect(() => {
    const loadParticipants = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setParticipants(mockParticipants);
      } catch (err) {
        setError('Failed to load participants');
      } finally {
        setLoading(false);
      }
    };

    loadParticipants();
  }, [eventId]);

  // Calculate stats
  const stats: ParticipantStats = {
    total: participants.length,
    reviewees: participants.filter(p => p.registrationType === 'reviewee').length,
    reviewers: participants.filter(p => p.registrationType === 'reviewer').length,
    pending: participants.filter(p => p.status === 'pending').length,
    approved: participants.filter(p => p.status === 'approved').length,
    rejected: participants.filter(p => p.status === 'rejected').length,
  };

  // Filter participants
  const filteredParticipants = participants.filter(participant => {
    const matchesSearch = searchTerm === '' || 
      participant.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      participant.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      participant.user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || participant.status === statusFilter;
    const matchesType = typeFilter === 'all' || participant.registrationType === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Handle participant action
  const handleParticipantAction = async (action: 'approve' | 'reject') => {
    if (!selectedParticipant) return;

    try {
      setLoading(true);
      setError(null);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update participant status
      setParticipants(prev => prev.map(p => 
        p.id === selectedParticipant.id 
          ? {
              ...p,
              status: action === 'approve' ? 'approved' : 'rejected',
              ...(action === 'approve' ? {
                approvedAt: new Date(),
                approvedBy: 'current-admin-id'
              } : {
                rejectedAt: new Date(),
                rejectedBy: 'current-admin-id',
                notes: actionNotes
              })
            }
          : p
      ));

      setShowConfirmDialog(false);
      setSelectedParticipant(null);
      setActionNotes('');
      setActionType(null);
    } catch (err) {
      setError('Failed to update participant status');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (participant: ParticipantRegistration, action: 'approve' | 'reject') => {
    setSelectedParticipant(participant);
    setActionType(action);
    setShowConfirmDialog(true);
  };

  const toggleDropdown = (participantId: string) => {
    setOpenDropdown(openDropdown === participantId ? null : participantId);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'reviewer': return 'info';
      case 'reviewee': return 'default';
      default: return 'default';
    }
  };

  if (loading && participants.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Event Participants</h2>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.reviewees}</div>
            <div className="text-sm text-gray-600">Reviewees</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.reviewers}</div>
            <div className="text-sm text-gray-600">Reviewers</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-emerald-600">{stats.approved}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search participants by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="reviewee">Reviewees</option>
              <option value="reviewer">Reviewers</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert type="error" title="Error" message={error} />
      )}

      {/* Participants List */}
      {filteredParticipants.length === 0 ? (
        <EmptyState
          title="No participants found"
          description={searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
            ? "No participants match your current filters." 
            : "No participants have registered for this event yet."}
          action={searchTerm || statusFilter !== 'all' || typeFilter !== 'all' ? {
            label: "Clear Filters",
            onClick: () => {
              setSearchTerm('');
              setStatusFilter('all');
              setTypeFilter('all');
            }
          } : undefined}
        />
      ) : (
        <div className="bg-white shadow overflow-visible sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredParticipants.map((participant) => (
              <li key={participant.id} className="px-6 py-4 relative overflow-visible">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <Users className="h-5 w-5 text-gray-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-lg font-medium text-gray-900">
                          {participant.user.firstName} {participant.user.lastName}
                        </h4>
                        <Badge variant={getStatusColor(participant.status) as any}>
                          {participant.status}
                        </Badge>
                        <Badge variant={getTypeColor(participant.registrationType) as any}>
                          {participant.registrationType}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {participant.user.email}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Registered: {participant.registeredAt.toLocaleDateString()}
                        </div>
                        {participant.approvedAt && (
                          <div className="flex items-center">
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approved: {participant.approvedAt.toLocaleDateString()}
                          </div>
                        )}
                        {participant.rejectedAt && (
                          <div className="flex items-center">
                            <UserX className="w-4 h-4 mr-1" />
                            Rejected: {participant.rejectedAt.toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="relative ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleDropdown(participant.id)}
                      className="text-gray-600 hover:text-gray-700"
                      title="More actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>

                    {openDropdown === participant.id && (
                      <>
                        {/* Backdrop to close dropdown */}
                        <div
                          className="fixed inset-0 z-30"
                          onClick={closeDropdown}
                        />
                        
                        {/* Dropdown menu */}
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-40">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setSelectedParticipant(participant);
                                setShowDetailsModal(true);
                                closeDropdown();
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Edit className="w-4 h-4 mr-3" />
                              View Details
                            </button>
                            
                            {participant.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    openActionDialog(participant, 'approve');
                                    closeDropdown();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                                >
                                  <UserCheck className="w-4 h-4 mr-3" />
                                  Approve
                                </button>
                                
                                <button
                                  onClick={() => {
                                    openActionDialog(participant, 'reject');
                                    closeDropdown();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <UserX className="w-4 h-4 mr-3" />
                                  Reject
                                </button>
                              </>
                            )}
                            
                            {participant.status !== 'pending' && (
                              <button
                                onClick={() => {
                                  openActionDialog(participant, participant.status === 'approved' ? 'reject' : 'approve');
                                  closeDropdown();
                                }}
                                className={`flex items-center w-full px-4 py-2 text-sm ${
                                  participant.status === 'approved'
                                    ? 'text-red-600 hover:bg-red-50'
                                    : 'text-green-600 hover:bg-green-50'
                                }`}
                              >
                                {participant.status === 'approved' ? (
                                  <>
                                    <UserX className="w-4 h-4 mr-3" />
                                    Reject
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="w-4 h-4 mr-3" />
                                    Approve
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Participant Details Modal */}
      {selectedParticipant && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedParticipant(null);
          }}
          title="Participant Details"
        >
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                <Users className="h-8 w-8 text-gray-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedParticipant.user.firstName} {selectedParticipant.user.lastName}
                </h3>
                <p className="text-gray-600">{selectedParticipant.user.email}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <Badge variant={getStatusColor(selectedParticipant.status) as any}>
                    {selectedParticipant.status}
                  </Badge>
                  <Badge variant={getTypeColor(selectedParticipant.registrationType) as any}>
                    {selectedParticipant.registrationType}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Account Created</label>
                <p className="text-sm text-gray-900">{selectedParticipant.user.createdAt.toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Registration Date</label>
                <p className="text-sm text-gray-900">{selectedParticipant.registeredAt.toLocaleDateString()}</p>
              </div>
              {selectedParticipant.approvedAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Approved Date</label>
                  <p className="text-sm text-gray-900">{selectedParticipant.approvedAt.toLocaleDateString()}</p>
                </div>
              )}
              {selectedParticipant.rejectedAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rejected Date</label>
                  <p className="text-sm text-gray-900">{selectedParticipant.rejectedAt.toLocaleDateString()}</p>
                </div>
              )}
            </div>

            {selectedParticipant.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{selectedParticipant.notes}</p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setSelectedParticipant(null);
          setActionNotes('');
          setActionType(null);
        }}
        onConfirm={() => handleParticipantAction(actionType!)}
        title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Participant`}
        message={`Are you sure you want to ${actionType} ${selectedParticipant?.user.firstName} ${selectedParticipant?.user.lastName}'s registration?`}
        confirmText={actionType === 'approve' ? 'Approve' : 'Reject'}
        type={actionType === 'approve' ? 'info' : 'danger'}
      />

      {/* Rejection Notes Modal */}
      {actionType === 'reject' && (
        <Modal
          isOpen={showConfirmDialog}
          onClose={() => {
            setShowConfirmDialog(false);
            setSelectedParticipant(null);
            setActionNotes('');
            setActionType(null);
          }}
          title="Reject Participant"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to reject {selectedParticipant?.user.firstName} {selectedParticipant?.user.lastName}'s registration?
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection (optional)
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Provide a reason for rejection..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setSelectedParticipant(null);
                  setActionNotes('');
                  setActionType(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleParticipantAction('reject')}
              >
                Reject Participant
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ParticipantsConfiguration;
